"use client";

import { useEffect, useState, useCallback } from 'react';
import UpdatePrompt from '@/components/UpdatePrompt';

/**
 * Registers `/sw.js` and surfaces a “New version available” banner when a waiting
 * worker is ready — **production deploys only**.
 *
 * Local `next dev` (and other non-production builds) skip SW registration entirely so
 * day-to-day code edits don’t keep flashing “Update now”. Bump `package.json` version
 * + `public/sw.js` `CACHE_BUST` when shipping a push you want clients to notice.
 */
export default function ServiceWorkerRegistration() {
  const [needRefresh, setNeedRefresh] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    // Dev / local iteration: drop any previously registered SW so leftover workers
    // from a prior session don’t keep flashing “Update now” while coding.
    if (process.env.NODE_ENV !== 'production') {
      navigator.serviceWorker.getRegistrations().then((regs) => {
        for (const reg of regs) void reg.unregister();
      }).catch(() => {});
      return;
    }

    let cancelled = false;
    let onVisible: (() => void) | null = null;
    let onUpdateFound: (() => void) | null = null;
    let registeredRegistration: ServiceWorkerRegistration | null = null;

    function onInstalled(worker: ServiceWorker) {
      worker.addEventListener('statechange', () => {
        if (worker.state === 'installed' && navigator.serviceWorker.controller) {
          if (!cancelled) {
            setWaitingWorker(worker);
            setNeedRefresh(true);
          }
        }
      });
    }

    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        if (cancelled) return;

        if (registration.waiting && navigator.serviceWorker.controller) {
          setWaitingWorker(registration.waiting);
          setNeedRefresh(true);
        }

        registeredRegistration = registration;
        onUpdateFound = () => {
          const installing = registration.installing;
          if (installing) onInstalled(installing);
        };
        registration.addEventListener('updatefound', onUpdateFound);

        onVisible = () => {
          if (document.visibilityState === 'visible') {
            registration.update().catch(() => {});
          }
        };
        document.addEventListener('visibilitychange', onVisible);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      if (onVisible) document.removeEventListener('visibilitychange', onVisible);
      if (registeredRegistration && onUpdateFound) {
        registeredRegistration.removeEventListener('updatefound', onUpdateFound);
      }
    };
  }, []);

  const applyUpdate = useCallback(() => {
    waitingWorker?.postMessage({ type: 'SKIP_WAITING' });
    setNeedRefresh(false);
    const reload = () => window.location.reload();
    navigator.serviceWorker.addEventListener('controllerchange', reload, { once: true });
    setTimeout(reload, 800);
  }, [waitingWorker]);

  if (!needRefresh) return null;

  return (
    <UpdatePrompt
      onUpdate={applyUpdate}
      onDismiss={() => setNeedRefresh(false)}
    />
  );
}
